import { Box } from "@mui/material";
import { DotLegalSelect, DotLegalSwitch, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import TrialUsageWarning from "@common/components/TrialUsageWarning";
import MentionInput from "@common/components/mentionInput/MentionInput";
import { EntityType } from "@common/Common.types";
import ActivityFormBox from "./ActivityFormBox";
import ActivityFieldLabel from "./ActivityFieldLabel";
import { ActivityBasicsSectionProps, useActivityBasicsSection } from "./ActivityBasicsSection.hooks";
import { useActivityFormStyles } from "./ActivityForm.styles";
import { useRiskIndicator } from "../../riskAssessment/riskIndicator/RiskIndicator.hooks";

function ActivityBasicsSection(props: ActivityBasicsSectionProps) {
    const { translateString } = useTranslation();
    const styles = useActivityFormStyles();
    const { getRiskIcon } = useRiskIndicator();
    const { priorityOptions, showTitleError, onNameChange, onDescriptionChange, onCustomIdChange, onPriorityChange, onDocumentationRequiredChange } =
        useActivityBasicsSection(props);

    return (
        <ActivityFormBox title={translateString("activityFormBasics")} startsWithFieldLabel={!props.showTrialWarning}>
            {props.showTrialWarning && <TrialUsageWarning textKey={"createAnnualWheelTrialWarning"} />}

            <Box sx={styles.idAndTitle}>
                <DotLegalTextField
                    label={translateString("id")}
                    toolTipText={translateString("customIdToolTip")}
                    value={props.model.customId ?? null}
                    debounce={false}
                    disabled={props.readOnly}
                    onChange={onCustomIdChange}
                    useFixedLabel
                />
                <DotLegalTextField
                    autoFocus
                    label={translateString("title")}
                    value={props.model.name}
                    debounce={false}
                    disabled={props.readOnly}
                    onChange={onNameChange}
                    errorText={showTitleError ? translateString("fieldRequired") : undefined}
                    useFixedLabel
                />
            </Box>

            <MentionInput
                multiline
                rows={5}
                debounce
                disabled={props.readOnly}
                useFixedLabel
                label={translateString("description")}
                placeholder={translateString("mentionTriggers")}
                value={props.model.description ?? undefined}
                onChange={onDescriptionChange}
                manageRelationsExternal
                mentionSource={{ entityFromId: props.annualWheelActivityId ?? "", entityFromType: EntityType.AnnualWheelActivity }}
            />

            <Box sx={styles.twoColumns}>
                <DotLegalSelect
                    label={translateString("priority")}
                    options={priorityOptions}
                    selectedItem={props.model.priority?.toString() ?? ""}
                    onChange={onPriorityChange}
                    placeholder={""}
                    noOptionsLabel={translateString("noOptions")}
                    disableClearable
                    disabled={props.readOnly}
                    useFixedLabel
                    renderOptions={(option) => (
                        <Box sx={styles.priorityOption}>
                            {getRiskIcon(Number(option.id))}
                            {option.name}
                        </Box>
                    )}
                    inputStartAdornment={props.model.priority ? getRiskIcon(props.model.priority) : undefined}
                />
            </Box>

            {!props.model.isProcessingActivityValidation && (
                <>
                    <ActivityFieldLabel label={translateString("documentationRequired")} disabled={props.readOnly} />
                    <DotLegalSwitch
                        checked={props.model.documentationRequired}
                        onChange={onDocumentationRequiredChange}
                        rightLabel={translateString("obligationDocumentationRequiredForEachTask")}
                        disabled={props.readOnly}
                        greyUncheckedTrack
                    />
                </>
            )}
        </ActivityFormBox>
    );
}

export default ActivityBasicsSection;
